import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

const root = window.Shopify?.routes?.root || '/';
const key = 'elirena:sp:declined';
let declined = false;
try { declined = localStorage.getItem(key) === '1'; } catch {}
let queue = Promise.resolve();
let scheduled = false;
let dirty = false;
const pendingUpdates = new Set();
let checkoutReady = false;
const rows = () => [...document.querySelectorAll('[data-elirena-protection]')];
const busy = (value) => rows().forEach(row => {
  row.classList.toggle('is-busy', value);
  row.setAttribute('aria-busy', String(value));
  row.querySelector('input').disabled = value;
});
function showError() {
  rows().forEach(row => { row.querySelector('[data-protection-error]').hidden = false; });
}
async function reconcile() {
  // Quantity changes include a section morph after their AJAX event settles.
  await Promise.allSettled([
    ...pendingUpdates,
    ...(customElements.get('cart-items-component')?.pendingQuantityUpdates || []),
  ]);
  const row = rows()[0];
  if (!row) return;
  busy(true);
  rows().forEach(node => { node.querySelector('input').checked = node.dataset.shippable === 'true' && !declined; });
  try {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    const protection = cart.items.filter(item => item.handle === 'ochrona-przesylki');
    const shippable = cart.items.some(item => item.handle !== 'ochrona-przesylki' && item.requires_shipping);
    const desired = shippable && !declined ? 1 : 0;
    rows().forEach(node => {
      node.hidden = !shippable;
      node.querySelector('input').checked = !!desired;
      node.querySelector('label').classList.toggle('is-on', !!desired);
    });
    const updates = {};
    protection.forEach((item, index) => {
      const quantity = index === 0 ? desired : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (!protection.length && desired) updates[row.dataset.variant] = 1;
    if (!Object.keys(updates).length) return;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId))];
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
    event.elirenaProtection = true;
    document.dispatchEvent(event);
    try {
      const update = await fetch(`${root}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections, sections_url: location.pathname }),
      });
      const result = await update.json();
      if (!update.ok || result.errors) throw new Error('Cart update failed');
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result), detail: {
        sections: result.sections, items: result.items, itemCount: result.item_count, source: 'elirena-protection', didError: false,
      } });
    } catch (error) { deferred.reject(error); throw error; }
  } catch (error) {
    showError();
    throw error;
  } finally { busy(false); }
}
function schedule() {
  dirty = true;
  if (scheduled) return queue;
  scheduled = true;
  queue = queue.catch(() => {}).then(async () => {
    try {
      while (dirty) {
        dirty = false;
        await reconcile();
      }
    } finally { scheduled = false; }
  });
  return queue;
}
function afterCartUpdate(event) {
  if (event.elirenaProtection || event.detail?.data?.source === 'elirena-protection') return;
  const pending = Promise.resolve(event.promise || event.detail?.promise);
  pendingUpdates.add(pending);
  pending.catch(() => {}).finally(() => {
    pendingUpdates.delete(pending);
    requestAnimationFrame(() => schedule().catch(() => {}));
  });
}
// This export dispatches shopify:cart:lines-update before add/change/remove,
// with a promise for the AJAX result. Older Horizon sections use cart:update.
for (const name of new Set([StandardEvents.cartLinesUpdate, 'cart:update', 'cart:add', 'cart:updated'])) {
  document.addEventListener(name, afterCartUpdate);
}
document.addEventListener(ThemeEvents.cartSectionRestored, afterCartUpdate);
// Empty drawers have no control row. Observe only structural cart changes so
// hydration/view transitions cannot race the event's promise. Ignore our own
// checked/hidden/busy attributes to avoid a reconciliation loop.
const cartNodes = 'cart-items-component, [data-elirena-protection], .cart-items__table-row, .cart-drawer__inner';
const observer = new MutationObserver(records => {
  if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
    node instanceof Element && (node.matches(cartNodes) || node.querySelector(cartNodes))
  ))) schedule().catch(() => {});
});
observer.observe(document.body, { childList: true, subtree: true });
document.addEventListener('change', event => {
  if (!event.target.matches('[data-protection-toggle]')) return;
  declined = !event.target.checked;
  try { localStorage.setItem(key, declined ? '1' : '0'); } catch {}
  schedule().catch(() => {});
});
// Capture also works with drawer implementations whose open event does not bubble.
document.addEventListener('theme-drawer:open', event => {
  if (event.target.id === 'cart-drawer') schedule().catch(() => {});
}, true);
window.addEventListener('pageshow', () => schedule().catch(() => {}));
document.addEventListener('submit', async event => {
  if (event.submitter?.name !== 'checkout' || checkoutReady || !rows().length) return;
  event.preventDefault();
  const form = event.target;
  const button = event.submitter;
  button.disabled = true;
  try {
    await schedule();
    checkoutReady = true;
    button.disabled = false;
    // Section rendering may replace the original form while protection is added.
    const current = document.querySelector('#cart-form') || form;
    const checkout = document.querySelector('button[name="checkout"]') || button;
    current.requestSubmit(checkout);
  } catch { showError(); } finally { button.disabled = false; checkoutReady = false; }
}, true);
schedule().catch(() => {});
// Desktop Horizon drawers retain their native focus handling and use an overlay.
document.addEventListener('click', event => {
  const drawer = document.querySelector('#cart-drawer[open]');
  if (event.target === drawer) drawer.close();
});
const locale = document.documentElement.lang;
document.querySelectorAll('footer .payment-icons__item').forEach(item => {
  const provider = item.querySelector('title')?.textContent.toLowerCase().replace(/[ _-]/g, '') || '';
  if ((provider === 'blik' && locale !== 'pl') || (provider === 'mbway' && locale !== 'pt-PT') || (['mobilepay', 'dankort'].includes(provider) && locale !== 'da')) item.hidden = true;
});
